import { Exception } from '@poppinss/utils'
import { isSimpleIdentifier } from './utils/odata'
import { ODataContext } from './services/odata_context_service'
import { negotiateContent, negotiateMetadata } from './utils/http'
import { useODataContext } from '@nhtio/lucid-resourceful/odata/context'
import { ODataErrorMiddleware } from './middlewares/odata_error_middleware'
import { ODataMetadataService, makeOpName } from './services/odata_metadata_service'
import { ResourcefulModelController } from './controllers/resourceful_model_controller'
import { isAdditionalResourcefulRouteDetails } from '../router_macro/utils/type_guards'
import { ResourcefulHttpContext } from '../router_macro/services/resourceful_router_context_service'
import { ResourcefulRouterOptionsService } from '../router_macro/services/resourceful_router_options_service'
import { ResourcefulRouterModelResolverService } from '../router_macro/services/resourceful_router_model_resolver_service'
import { ResourcefulRouterModelServiceOptionsService } from '../router_macro/services/resourceful_router_model_options_service'
import {
  E_ROUTE_NOT_FOUND,
  E_UNRESOLVABLE_MODEL,
  E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE,
  //   E_INVALID_HTTP_METHOD_EXCEPTION,
  E_UNACCEPTABLE_FORMAT,
  E_INVALID_ENTITYSET_IDENTIFIERS,
} from '@nhtio/lucid-resourceful/errors'
import type { ResourcefulHttpVerb } from '@nhtio/lucid-resourceful/types'
import type { HttpContext, Router, RouteGroup } from '@adonisjs/core/http'
import type { ODataResponseFormat, ODataMetadataLevel } from './utils/http'
import type { MacroModelsMap, ResourcefulRouterOptions, RouteCallback } from '../router_macro/types'
import type { ResourcefulHttpContextResolver } from '../router_macro/services/resourceful_router_context_service'
import type {
  ResourcefulODataContextResolver,
  ResolvedODataContext,
} from './services/odata_context_service'

type ODataRouteCallback = (ctx: HttpContext) => Promise<void> | void

class ODataRouterService {
  readonly #router: Router
  readonly #rawModelMap: MacroModelsMap
  readonly #options?: Partial<ResourcefulRouterOptions>
  readonly #opts: ResourcefulRouterOptionsService
  readonly #modelMap: ResourcefulRouterModelResolverService
  readonly #controllers: Map<string, ResourcefulModelController>
  readonly #group: RouteGroup
  readonly #routes: Map<ResourcefulHttpVerb, Map<string | RegExp, ODataRouteCallback>>

  /** @private */
  constructor(
    router: Router,
    rawModelMap: MacroModelsMap,
    options?: Partial<ResourcefulRouterOptions>
  ) {
    this.#router = router
    this.#rawModelMap = rawModelMap
    this.#options = options
    this.#controllers = new Map<string, ResourcefulModelController>()
    this.#opts = ResourcefulRouterOptionsService.init(this.#options)
    this.#modelMap = ResourcefulRouterModelResolverService.init(this.#rawModelMap)
    const unresolvableModels: E_UNRESOLVABLE_MODEL[] = []
    const invalidEntitySetNames: Set<string> = new Set<string>()
    Object.getOwnPropertyNames(this.#modelMap).forEach((name) => {
      const promiseOrError = this.#modelMap[name]
      if (!isSimpleIdentifier(name)) {
        invalidEntitySetNames.add(name)
        return
      }
      if (promiseOrError instanceof E_UNRESOLVABLE_MODEL) {
        unresolvableModels.push(promiseOrError)
      } else if ('undefined' !== typeof promiseOrError) {
        const modelOptions = ResourcefulRouterModelServiceOptionsService.init(
          this.#rawModelMap[name as any]
        )
        const controller = new ResourcefulModelController(
          name,
          promiseOrError,
          modelOptions,
          this.#opts.headers,
          this.#opts.scopeRestrictors,
          this.#opts.payloadRestrictors,
          this.#opts.mutators,
          this.#opts.policies
        )
        this.#controllers.set(name, controller)
      }
    })
    if (unresolvableModels.length > 0) {
      throw new E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE(unresolvableModels)
    }
    if (invalidEntitySetNames.size > 0) {
      throw new E_INVALID_ENTITYSET_IDENTIFIERS(invalidEntitySetNames)
    }
    this.#group = this.#router.group(this.#groupCallback.bind(this))
    if (this.#opts.prefix !== '/') {
      this.#group.prefix(this.#opts.prefix)
    }
    if (Array.isArray(this.#opts.middleware) && this.#opts.middleware.length > 0) {
      this.#group.use(Array.from(this.#opts.middleware))
    } else if (!Array.isArray(this.#opts.middleware) && this.#opts.middleware) {
      this.#group.use(this.#opts.middleware as any)
    }
    if ('string' === typeof this.#opts.domain && this.#opts.domain.length > 0) {
      this.#group.domain(this.#opts.domain)
    }
    this.#routes = new Map<ResourcefulHttpVerb, Map<string | RegExp, ODataRouteCallback>>()
    this.#initRoutes()
  }

  #initRoutes() {
    const getRoutes = new Map<string | RegExp, ODataRouteCallback>()
    const postRoutes = new Map<string | RegExp, ODataRouteCallback>()
    const putRoutes = new Map<string | RegExp, ODataRouteCallback>()
    const patchRoutes = new Map<string | RegExp, ODataRouteCallback>()
    const deleteRoutes = new Map<string | RegExp, ODataRouteCallback>()
    Array.from(this.#controllers.entries())
      .sort((a, b) => {
        if (a[0].length === b[0].length) {
          return a[0].localeCompare(b[0])
        }
        return b[0].length - a[0].length
      })
      .forEach(([name, controller]) => {
        const currentControllerCan = (op: ResourcefulRouterOptions['except'][number]) => {
          return !this.#opts.except.includes(op) && !controller.options.except.includes(op)
        }
        if (currentControllerCan('index')) {
          const pattern = new RegExp(`^\/${name}\/?(?:\\$\\w+)*$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelIndex.bind(controller)
          )
          getRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
          getRoutes.set(
            `/${name}/$count`,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('create')) {
          const pattern = `/${name}`
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelCreate.bind(controller)
          )
          postRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('read')) {
          const pattern = new RegExp(`^\/${name}\\(([^)]+)\\)$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelRead.bind(controller)
          )
          getRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('readRelated')) {
          const pattern = new RegExp(`^\/${name}\\(([^)]+)\\)\/\\w{1,128}\/?(?:\\$\\w+)*$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelReadRelated.bind(controller)
          )
          getRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('syncRelated')) {
          const pattern = new RegExp(`^\/${name}\\(([^)]+)\\)\/Default\\.Sync(?:\\w+)+$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelSyncRelated.bind(controller)
          )
          postRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('update')) {
          const pattern = new RegExp(`^\/${name}\\(([^)]+)\\)$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelUpdate.bind(controller)
          )
          postRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
          putRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
          patchRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        if (currentControllerCan('delete')) {
          const pattern = new RegExp(`^\/${name}\\(([^)]+)\\)$`)
          const callback = this.#processOdataRequest.bind(
            this,
            controller.serveModelDelete.bind(controller)
          )
          deleteRoutes.set(
            pattern,
            this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
          )
        }
        Object.entries(controller.options.additional).forEach(([methodAndPath]) => {
          const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
          if (!method || !path) return
          const opKind = ['post', 'patch', 'put', 'delete'].includes(method) ? 'action' : 'function'
          const boundToInstance = path.includes(':uid\/')
          const pattern = boundToInstance
            ? opKind === 'action'
              ? new RegExp(
                  `^\/${name}\\(([^)]+)\\)\/Default\\.(Get|Post|Put|Patch|Delete)[a-zA-Z0-9_]+$`
                )
              : new RegExp(
                  `^\/${name}\\(([^)]+)\\)\/Default\\.(Get|Post|Put|Patch|Delete)[a-zA-Z0-9_]+\\(\\)$`
                )
            : opKind === 'action'
              ? new RegExp(`^\/${name}\/Default\\.(Get|Post|Put|Patch|Delete)[a-zA-Z0-9_]+$`)
              : new RegExp(`^\/${name}\/Default\\.(Get|Post|Put|Patch|Delete)[a-zA-Z0-9_]+\\(\\)$`)
          const callback = boundToInstance
            ? 'action' === opKind
              ? this.#processOdataRequest.bind(
                  this,
                  controller.serveModelInstanceAction.bind(controller)
                )
              : this.#processOdataRequest.bind(
                  this,
                  controller.serveModelInstanceFunction.bind(controller)
                )
            : 'action' === opKind
              ? this.#processOdataRequest.bind(this, controller.serveModelAction.bind(controller))
              : this.#processOdataRequest.bind(this, controller.serveModelFunction.bind(controller))
          switch (opKind) {
            case 'action':
              postRoutes.set(
                pattern,
                this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
              )
              break
            case 'function':
              getRoutes.set(
                pattern,
                this.#serveInResourcefulModelRouterContext.bind(this, controller, callback)
              )
              break
          }
        })
      })
    this.#routes.set('get', getRoutes)
    this.#routes.set('post', postRoutes)
    this.#routes.set('put', putRoutes)
    this.#routes.set('patch', patchRoutes)
    this.#routes.set('delete', deleteRoutes)
  }

  #groupCallback() {
    Object.entries(this.#opts.additional).forEach(([methodAndPath, callback]) => {
      const [method, path] = methodAndPath.split('|') as [ResourcefulHttpVerb, string]
      if (!method || !path) return
      const opName = makeOpName(method, path)
      // unbound actions
      const routerMethod = ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())
        ? 'post'
        : 'get'
      const opPath = ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase())
        ? `/Default.${opName}`
        : `/Default.${opName}()`
      if (isAdditionalResourcefulRouteDetails(callback)) {
        this.#router[routerMethod](opPath, this.#processOdataRequest.bind(this, callback.handler))
      } else {
        this.#router[routerMethod](opPath, this.#processOdataRequest.bind(this, callback))
      }
    })
    this.#router.get(
      '/$metadata',
      this.#processOdataRequest.bind(this, this.#serveRootMetadata.bind(this))
    )
    this.#router.get(
      '/',
      this.#processOdataRequest.bind(this, this.#serveRootServiceDocument.bind(this))
    )
    // Explicitly route non-GET root methods through OData path resolution so they return 405.
    this.#router.post(
      '/',
      this.#processOdataRequest.bind(this, this.#serveResourceRequest.bind(this))
    )
    this.#router.put(
      '/',
      this.#processOdataRequest.bind(this, this.#serveResourceRequest.bind(this))
    )
    this.#router.patch(
      '/',
      this.#processOdataRequest.bind(this, this.#serveResourceRequest.bind(this))
    )
    this.#router.delete(
      '/',
      this.#processOdataRequest.bind(this, this.#serveResourceRequest.bind(this))
    )
    this.#router.any(
      '/*',
      this.#processOdataRequest.bind(this, this.#serveResourceRequest.bind(this))
    )
  }

  async #processRequest(callback: RouteCallback, ctx: HttpContext) {
    let next: ((ctx: HttpContext) => unknown | PromiseLike<unknown> | Promise<unknown>) | undefined
    if (typeof callback === 'function') {
      next = async (c) => await callback(c)
    } else if (Array.isArray(callback) && callback.length === 2) {
      const [maybeResolvableController, method] = callback
      if (
        method in maybeResolvableController &&
        typeof maybeResolvableController[method] === 'function'
      ) {
        // @ts-ignore
        next = async (c) => await maybeResolvableController[method](c)
      } else {
        // @ts-ignore
        const resolvedController = maybeResolvableController()
        if (typeof resolvedController[method] === 'function') {
          next = async (c) => await resolvedController[method](c)
        }
      }
    }
    if (!next) return
    try {
      await next(ctx)
    } catch (error) {
      if (this.#opts.catchThrown && ODataErrorMiddleware.shouldHandle(ctx)) {
        return await ODataErrorMiddleware.handle(error, ctx, {
          onVineValidationError: this.#opts.onVineValidationError,
          onJoiValidationError: this.#opts.onJoiValidationError,
          onException: this.#opts.onException,
          onError: this.#opts.onError,
          onUnknown: this.#opts.onUnknown,
          onAny: this.#opts.onAny,
          asYaml: true,
          headers: this.#opts.headers,
        })
      } else {
        throw error
      }
    }
  }

  async #serveInODataContext(
    callback: RouteCallback,
    fallbackResponseFormat: ODataResponseFormat,
    fallbackMetadataLevel: ODataMetadataLevel,
    ctx: HttpContext
  ) {
    const resolver: ResourcefulODataContextResolver = this.#resolvedODataContext.bind(
      this,
      ctx,
      fallbackResponseFormat,
      fallbackMetadataLevel
    )
    return await ODataContext.run({ resolver }, async () => {
      if (typeof callback === 'function') {
        return await callback(ctx)
      } else if (Array.isArray(callback) && callback.length === 2) {
        const [maybeResolvableController, method] = callback
        if (
          method in maybeResolvableController &&
          typeof maybeResolvableController[method] === 'function'
        ) {
          // @ts-ignore
          await maybeResolvableController[method](ctx)
        } else {
          // @ts-ignore
          const resolvedController = maybeResolvableController()
          if (typeof resolvedController[method] === 'function') {
            await resolvedController[method](ctx)
          }
        }
      }
    })
  }

  async #processOdataRequest(
    callback: RouteCallback,
    ctx: HttpContext,
    fallbackResponseFormat: ODataResponseFormat = 'json',
    fallbackMetadataLevel: ODataMetadataLevel = 'minimal'
  ) {
    return await this.#processRequest(
      this.#serveInODataContext.bind(
        this,
        callback,
        fallbackResponseFormat,
        fallbackMetadataLevel,
        ctx
      ),
      ctx
    )
  }

  async #resolvedODataContext(
    ctx: HttpContext,
    fallbackResponseFormat: ODataResponseFormat = 'json',
    fallbackMetadataLevel: ODataMetadataLevel = 'minimal'
  ): Promise<ResolvedODataContext> {
    const fullUrl = ctx.request.completeUrl()
    const indexOfPrefix = fullUrl.indexOf(this.#opts.prefix)
    const beforePrefix = fullUrl.substring(0, indexOfPrefix === -1 ? fullUrl.length : indexOfPrefix)
    const ret: ResolvedODataContext = {
      serviceBaseUrl: beforePrefix + this.#opts.prefix,
      serveAs: negotiateContent(ctx, fallbackResponseFormat),
      metadataLevel: negotiateMetadata(ctx, fallbackMetadataLevel),
    }
    ctx.response.header('OData-Version', '4.0')
    return ret
  }

  async #serveInResourcefulModelRouterContext(
    controller: ResourcefulModelController,
    callback: RouteCallback,
    ctx: HttpContext
  ) {
    const resolver: ResourcefulHttpContextResolver = controller.resolveRequestRequirements.bind(
      controller,
      ctx
    ) as ResourcefulHttpContextResolver
    await ResourcefulHttpContext.run({ resolver }, async () => {
      if (typeof callback === 'function') {
        await callback(ctx)
      } else if (Array.isArray(callback) && callback.length === 2) {
        const [maybeResolvableController, method] = callback
        if (
          method in maybeResolvableController &&
          typeof maybeResolvableController[method] === 'function'
        ) {
          // @ts-ignore
          await maybeResolvableController[method](ctx)
        } else {
          // @ts-ignore
          const resolvedController = maybeResolvableController()
          if (typeof resolvedController[method] === 'function') {
            await resolvedController[method](ctx)
          }
        }
      }
    })
  }

  async #serveRootServiceDocument(ctx: HttpContext) {
    const { serveAs } = await useODataContext()
    if ('xml' === serveAs) {
      return await this.#serveRootServiceDocumentXml()
    } else {
      return await this.#serveRootServiceDocumentJson(ctx)
    }
  }

  async #serveRootServiceDocumentJson(ctx: HttpContext) {
    const payload = {
      '@odata.context': `$metadata`,
      'value': Array.from(this.#controllers.keys()).map((name) => ({
        name,
        kind: 'EntitySet',
        url: name,
      })),
    }
    return ctx.response.json(payload)
  }

  async #serveRootServiceDocumentXml() {
    throw new E_UNACCEPTABLE_FORMAT('xml')
  }

  async #serveRootMetadata(ctx: HttpContext) {
    const { serveAs } = await useODataContext()
    const renegotiated = negotiateContent(ctx, 'xml')
    if ('json' === serveAs && 'json' === renegotiated) {
      throw new E_UNACCEPTABLE_FORMAT('json')
    } else {
      const metadata = await ODataMetadataService.serveRootMetadata(
        this.#opts,
        this.#controllers,
        ctx
      )
      return ctx.response.oXml(200, metadata)
    }
  }

  async #serveResourceRequest(ctx: HttpContext) {
    // Normalize wildcard segment to a canonical path
    const star = ctx.params['*']
    let resource = Array.isArray(star)
      ? `/${star.join('/')}`
      : typeof star === 'string'
        ? star.startsWith('/')
          ? star
          : `/${star}`
        : '/'
    resource = resource.replace(/\/{2,}/g, '/')
    const method = (ctx.request.method() || 'GET').toLowerCase()

    // Service root supports read semantics only.
    if (resource === '/' && !['get', 'head'].includes(method)) {
      throw new Exception(`Method ${method.toUpperCase()} is not allowed for OData service root`, {
        status: 405,
        code: 'E_METHOD_NOT_ALLOWED',
      })
    }

    const effectiveMethod = (method === 'head' ? 'get' : method) as ResourcefulHttpVerb
    const routes = this.#routes.get(effectiveMethod)
    if (!routes) {
      throw new E_ROUTE_NOT_FOUND([method.toUpperCase(), ctx.request.url() || '/'])
    }
    // Exact match first (try as-is and without trailing slash)
    const trimmed = resource !== '/' ? resource.replace(/\/+$/, '') : resource
    const exact = routes.get(resource) || routes.get(trimmed)
    if (exact) {
      return await exact(ctx)
    }
    // Regex match: longest match wins, then most specific pattern (by regex source length)
    const candidates = trimmed === resource ? [resource] : [resource, trimmed]
    const matches = Array.from(routes.entries())
      .filter(([pattern]) => pattern instanceof RegExp)
      .map(([pattern, cb]) => {
        let best: { len: number; specif: number; cb: ODataRouteCallback } | null = null
        for (const path of candidates) {
          const m = (pattern as RegExp).exec(path)
          if (m) {
            const cur = { len: m[0].length, specif: (pattern as RegExp).source.length, cb }
            if (!best || cur.len > best.len || (cur.len === best.len && cur.specif > best.specif)) {
              best = cur
            }
          }
        }
        return best
      })
      .filter((v): v is { len: number; specif: number; cb: ODataRouteCallback } => !!v)
      .sort((a, b) => b.len - a.len || b.specif - a.specif)

    if (matches.length > 0) {
      return await matches[0].cb(ctx)
    }
    // No match
    throw new E_ROUTE_NOT_FOUND([method.toUpperCase(), ctx.request.url() || '/'])
  }

  get group() {
    return this.#group
  }

  public static hook(
    router: Router,
    rawModelMap: MacroModelsMap,
    options?: Partial<ResourcefulRouterOptions>
  ): RouteGroup {
    const instance = new ODataRouterService(router, rawModelMap, options)
    return instance.group
  }
}

export const odataRouterMacro: ODataRouterMacro = function (
  this: Router,
  models: MacroModelsMap,
  options: Partial<ResourcefulRouterOptions> = {}
): RouteGroup {
  return ODataRouterService.hook(this, models, options)
}

export interface ODataRouterMacro {
  (models: MacroModelsMap, options?: Partial<ResourcefulRouterOptions>): RouteGroup
}

export type { ODataRouterService }
